import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import {
  json,
  redirect,
  type DataFunctionArgs,
  type MetaFunction,
} from '@remix-run/node'
import { useFetcher } from '@remix-run/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '~/components/error-boundary.tsx'
import { StatusButton } from '~/components/ui/inputs/button/status-button.tsx'
import { prisma } from '~/utils/config/db.server.ts'
import { emailSchema } from '~/utils/user-validation.ts'
import { invariantResponse } from '~/utils/misc.tsx'
import { AuthLayout } from '~/components/layout/auth-layout.tsx'
import { Spacer } from '~/components/ui/spacer.tsx'
import { getRedirectToUrl, prepareVerification } from '~/features/auth/auth.ts'
import { sendEmail } from '~/utils/resend/email.server.ts'
import { ForgotPasswordEmail } from './email.server.tsx'
import { ErrorList, Field } from '~/components/ui/inputs/forms.tsx'
import { validateCSRF } from '~/utils/csrf.server.ts'
import { checkHoneypot } from '~/utils/honeypot.server.ts'
import { AuthenticityTokenInput } from 'remix-utils/csrf/react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'

const ForgotPasswordSchema = z.object({
  email: emailSchema,
})

export async function action({ request }: DataFunctionArgs) {
  const formData = await request.formData()
  await validateCSRF(formData, request.headers)
  checkHoneypot(formData)
  const submission = await parseWithZod(formData, {
    schema: ForgotPasswordSchema.superRefine(async (data, ctx) => {
      const user = await prisma.user.findFirst({
        where: {
          OR: [{ email: data.email }],
        },
        select: { id: true },
      })
      if (!user) {
        ctx.addIssue({
          path: ['email'],
          code: z.ZodIssueCode.custom,
          message: 'No existe usuario con este correo',
        })
        return
      }
    }),
    async: true,
  })
  if (submission.status !== 'success') {
    return json(
      { result: submission.reply() },
      { status: submission.status === 'error' ? 400 : 200 },
    )
  }

  const { email } = submission.value
  const redirectTo = getRedirectToUrl({
    request,
    type: 'forgot-password',
    target: email,
  })

  const user = await prisma.user.findFirst({
    where: { OR: [{ email: email }] },
    select: { email: true },
  })
  invariantResponse(user, 'El usuario deberia existir')

  const { verifyUrl, otp } = await prepareVerification({
    period: 10 * 60,
    request,
    type: 'forgot-password',
    target: email,
  })

  const response = await sendEmail({
    to: user.email,
    subject: `Recuperar contraseña de ITraack`,
    react: <ForgotPasswordEmail verifyUrl={verifyUrl.toString()} otp={otp} />,
  })

  if (response.status === 'success') {
    return redirect(redirectTo.toString())
  } else {
    return json(
      { result: submission.reply({ formErrors: [response.error.message] }) },
      { status: 500 },
    )
  }
}

export const meta: MetaFunction = () => {
  return [{ title: 'Password Recovery for Itraack' }]
}

export default function ForgotPasswordRoute() {
  const forgotPassword = useFetcher<typeof action>()

  const [form, fields] = useForm({
    id: 'forgot-password-form',
    constraint: getZodConstraint(ForgotPasswordSchema),
    lastResult: forgotPassword.data?.result,
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: ForgotPasswordSchema })
    },
    shouldRevalidate: 'onBlur',
  })

  return (
    <AuthLayout>
      <div className="flex flex-col justify-center">
        <h1 className="text-h4">¿Olvidaste tu contraseña?</h1>
        <p className="text-body-md mt-3 text-foreground-muted">
          No te preocupes, te enviaremos instrucciones para restablecerla.
        </p>
        <Spacer size="3xs" />
        <forgotPassword.Form method="POST" {...getFormProps(form)}>
          <AuthenticityTokenInput />
          <HoneypotInputs />
          <Field
            labelProps={{
              htmlFor: fields.email.id,
              children: 'Email',
            }}
            inputProps={{
              autoFocus: true,
              ...getInputProps(fields.email, { type: 'text' }),
              placeholder: 'Email',
            }}
            className="text-center [&>input]:rounded-full [&>input]:border-2 [&>input]:border-dashed [&>input]:border-primary [&>input]:bg-white [&>input]:text-center"
            errors={fields.email.errors}
          />
          <Spacer size="4xs" />
          <ErrorList errors={form.errors} id={form.errorId} />

          <div>
            <StatusButton
              className="w-full rounded-full"
              status={
                forgotPassword.state === 'submitting'
                  ? 'pending'
                  : form.status ?? 'idle'
              }
              type="submit"
              disabled={forgotPassword.state !== 'idle'}
            >
              Restablecer contraseña
            </StatusButton>
          </div>
        </forgotPassword.Form>
      </div>
    </AuthLayout>
  )
}

export function ErrorBoundary() {
  return <GeneralErrorBoundary />
}
